import {FC, Fragment, useCallback, useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router';
import SimpleBar from 'simplebar-react';

import {menuItemT, menuTitleT} from '@/lib/types/nav.types';

import {useApp} from '@/providers/components/appProvider';
import {useAuth} from '@/providers/components/authProvider';

import {
  ADMINMENUITEMS,
  DISTRIBUTORMENUITEMS,
  RETAILERMENUITEMS,
  SPECULATORMENUITEMS,
} from '@/components/common/sidebar/sidemenu/sidemenu';
import Menuloop from '@/components/ui/menuloop';

const Sidebar: FC = () => {
  const {theme, updateTheme, toggleSidebar} = useApp();
  const {isSuperAdmin, isAdmin, isAdministrator, actingAs, setActingAs, isDistributor, isRetailer} = useAuth();

  const location = useLocation();
  const [menuitems, setMenuitems] = useState<(menuItemT | menuTitleT)[]>(SPECULATORMENUITEMS);

  const currentPath = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;

  useEffect(() => {
    const distributorMenuItems = isDistributor ? [...DISTRIBUTORMENUITEMS] : [];
    const retailerMenuItems = isRetailer ? [...RETAILERMENUITEMS] : [];
    if (actingAs === 'admin' || actingAs === 'superAdmin') {
      setMenuitems([...ADMINMENUITEMS, ...distributorMenuItems, ...retailerMenuItems]);
    } else {
      setMenuitems([...SPECULATORMENUITEMS, ...distributorMenuItems, ...retailerMenuItems]);
    }
  }, [actingAs, isDistributor, isRetailer]);

  // function to handle switching between speculator and admin
  const handleToggleAccount = () => {
    if (actingAs === 'speculator') {
      setActingAs('admin');
    } else {
      setActingAs('speculator');
    }
  };

  const closeMenuFn = useCallback(() => {
    const closeMenuRecursively = (items: any) => {
      items?.forEach((item: any) => {
        item.active = false;
        closeMenuRecursively(item.children);
      });
    };
    closeMenuRecursively(ADMINMENUITEMS);
    setMenuitems((arr: any) => [...arr]);
  }, []);

  function Onhover() {
    if ((theme.toggled == 'icon-overlay-close' || theme.toggled == 'detached-close') && theme.iconOverlay != 'open') {
      updateTheme({iconOverlay: 'open'});
    }
  }
  function Outhover() {
    if ((theme.toggled == 'icon-overlay-close' || theme.toggled == 'detached-close') && theme.iconOverlay == 'open') {
      updateTheme({iconOverlay: ''});
    }
  }

  const menuClose = useCallback(() => {
    const html = document.documentElement;
    const currentToggled = html.getAttribute('data-toggled');

    if (window.innerWidth <= 992) {
      if (currentToggled !== 'close') {
        updateTheme({toggled: 'close'});
      }
    }

    const overlayElement = document.querySelector('#responsive-overlay') as HTMLElement | null;
    if (overlayElement?.classList.contains('active')) {
      overlayElement.classList.remove('active');
    }

    if (
      theme.dataNavLayout === 'horizontal' ||
      theme.dataNavStyle === 'menu-click' ||
      theme.dataNavStyle === 'icon-click'
    ) {
      closeMenuFn();
    }
  }, [theme.dataNavLayout, theme.dataNavStyle, updateTheme, closeMenuFn]);

  function switcherArrowFn(): void {
    // Used to remove is-expanded class and remove class on clicking arrow buttons
    function slideClick(): void {
      const slide = document.querySelectorAll<HTMLElement>('.slide');
      const slideMenu = document.querySelectorAll<HTMLElement>('.slide-menu');

      slide.forEach(element => {
        if (element.classList.contains('is-expanded')) {
          element.classList.remove('is-expanded');
        }
      });

      slideMenu.forEach(element => {
        if (element.classList.contains('open')) {
          element.classList.remove('open');
          element.style.display = 'none';
        }
      });
    }

    slideClick();
  }

  function slideRight(): void {
    const menuNav = document.querySelector<HTMLElement>('.main-menu');
    const mainContainer1 = document.querySelector<HTMLElement>('.main-sidebar');

    if (menuNav && mainContainer1) {
      const marginLeftValue = Math.ceil(Number(window.getComputedStyle(menuNav).marginInlineStart.split('px')[0]));
      const marginRightValue = Math.ceil(Number(window.getComputedStyle(menuNav).marginInlineEnd.split('px')[0]));
      const check = menuNav.scrollWidth - mainContainer1.offsetWidth;
      let mainContainer1Width = mainContainer1.offsetWidth;

      if (menuNav.scrollWidth > mainContainer1.offsetWidth) {
        if (!(theme.dir === 'rtl')) {
          if (Math.abs(check) > Math.abs(marginLeftValue)) {
            menuNav.style.marginInlineEnd = '0';

            if (!(Math.abs(check) > Math.abs(marginLeftValue) + mainContainer1Width)) {
              mainContainer1Width = Math.abs(check) - Math.abs(marginLeftValue);
              const slideRightButton = document.querySelector<HTMLElement>('#slide-right');
              if (slideRightButton) {
                slideRightButton.classList.add('hidden');
              }
            }

            menuNav.style.marginInlineStart =
              Number(menuNav.style.marginInlineStart.split('px')[0]) - Math.abs(mainContainer1Width) + 'px';

            const slideRightButton = document.querySelector<HTMLElement>('#slide-right');
            if (slideRightButton) {
              slideRightButton.classList.remove('hidden');
            }
          }
        } else {
          if (Math.abs(check) > Math.abs(marginRightValue)) {
            menuNav.style.marginInlineEnd = '0';

            if (!(Math.abs(check) > Math.abs(marginRightValue) + mainContainer1Width)) {
              mainContainer1Width = Math.abs(check) - Math.abs(marginRightValue);
              const slideRightButton = document.querySelector<HTMLElement>('#slide-right');
              if (slideRightButton) {
                slideRightButton.classList.add('hidden');
              }
            }

            menuNav.style.marginInlineStart =
              Number(menuNav.style.marginInlineStart.split('px')[0]) - Math.abs(mainContainer1Width) + 'px';

            const slideLeftButton = document.querySelector<HTMLElement>('#slide-left');
            if (slideLeftButton) {
              slideLeftButton.classList.remove('hidden');
            }
          }
        }
      }

      const element = document.querySelector<HTMLElement>('.main-menu > .slide.open');
      const element1 = document.querySelector<HTMLElement>('.main-menu > .slide.open > ul');
      if (element) {
        element.classList.remove('active');
      }
      if (element1) {
        element1.style.display = 'none';
      }
    }

    switcherArrowFn();
  }

  function slideLeft(): void {
    const menuNav = document.querySelector<HTMLElement>('.main-menu');
    const mainContainer1 = document.querySelector<HTMLElement>('.main-sidebar');

    if (menuNav && mainContainer1) {
      const marginLeftValue = Math.ceil(Number(window.getComputedStyle(menuNav).marginInlineStart.split('px')[0]));
      const marginRightValue = Math.ceil(Number(window.getComputedStyle(menuNav).marginInlineEnd.split('px')[0]));
      const check = menuNav.scrollWidth - mainContainer1.offsetWidth;
      let mainContainer1Width = mainContainer1.offsetWidth;

      if (menuNav.scrollWidth > mainContainer1.offsetWidth) {
        if (!(theme.dir === 'rtl')) {
          if (Math.abs(check) <= Math.abs(marginLeftValue)) {
            menuNav.style.marginInlineStart = '0px';
          }
        } else {
          if (Math.abs(check) > Math.abs(marginRightValue)) {
            menuNav.style.marginInlineStart = '0';

            if (!(Math.abs(check) > Math.abs(marginRightValue) + mainContainer1Width)) {
              mainContainer1Width = Math.abs(check) - Math.abs(marginRightValue);
              const slideRightButton = document.querySelector<HTMLElement>('#slide-right');
              if (slideRightButton) {
                slideRightButton.classList.add('hidden');
              }
            }

            menuNav.style.marginInlineStart =
              Number(menuNav.style.marginInlineStart.split('px')[0]) - Math.abs(mainContainer1Width) + 'px';

            const slideLeftButton = document.querySelector<HTMLElement>('#slide-left');
            if (slideLeftButton) {
              slideLeftButton.classList.remove('hidden');
            }
          }
        }
      }

      const element = document.querySelector<HTMLElement>('.main-menu > .slide.open');
      const element1 = document.querySelector<HTMLElement>('.main-menu > .slide.open > ul');
      if (element) {
        element.classList.remove('active');
      }
      if (element1) {
        element1.style.display = 'none';
      }
    }

    switcherArrowFn();
  }

  const Topup = () => {
    if (window.scrollY > 30 && document.querySelector('.app-sidebar')) {
      const Scolls = document.querySelectorAll('.app-sidebar');
      Scolls.forEach(e => {
        e.classList.add('sticky-pin');
      });
    } else {
      const Scolls = document.querySelectorAll('.app-sidebar');
      Scolls.forEach(e => {
        e.classList.remove('sticky-pin');
      });
    }
  };
  window.addEventListener('scroll', Topup);

  const level = 0;
  let hasParent = false;
  let hasParentLevel = 0;

  // Typeguard to filter out menuTitleTs

  function isMenuTitleTs(menuItem: menuItemT | menuTitleT): menuItem is menuTitleT {
    return (menuItem as menuTitleT).menutitle !== undefined;
  }

  function setSubmenu(event: any, targetObject: any, ADMINMENUITEMS = menuitems) {
    // const theme = store.getState().theme;
    if (
      (window.screen.availWidth <= 992 || theme.dataNavStyle != 'icon-hover') &&
      (window.screen.availWidth <= 992 || theme.dataNavStyle != 'menu-hover')
    ) {
      if (!event?.ctrlKey) {
        for (const item of ADMINMENUITEMS) {
          if (isMenuTitleTs(item)) continue;

          if (item === targetObject) {
            item.active = true;
            item.selected = true;
            // setMenuAncestorsActive(ADMINMENUITEMS,item);
            setMenuAncestorsActive(item);
          } else if (!item.active && !item.selected) {
            item.active = false; // Set active to false for items not matching the target
            item.selected = false; // Set active to false for items not matching the target
          } else {
            // removeActiveOtherMenus(ADMINMENUITEMS,item);
            removeActiveOtherMenus(item);
          }
          if (item.children && item.children.length > 0) {
            setSubmenu(event, targetObject, item.children);
          }
        }
      }
    }

    setMenuitems((arr: any) => [...arr]);
  }
  function getParentObject(obj: any, childObject: any) {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (typeof obj[key] === 'object' && JSON.stringify(obj[key]) === JSON.stringify(childObject)) {
          return obj; // Return the parent object
        }
        if (typeof obj[key] === 'object') {
          const parentObject: any = getParentObject(obj[key], childObject);
          if (parentObject !== null) {
            return parentObject;
          }
        }
      }
    }
    return null; // Object not found
  }
  function setMenuAncestorsActive(targetObject: any) {
    const parent = getParentObject(menuitems, targetObject);
    // const theme = store.getState().theme;
    if (parent) {
      if (hasParentLevel > 2) {
        hasParent = true;
      }
      parent.active = true;
      parent.selected = true;
      hasParentLevel += 1;
      setMenuAncestorsActive(parent);
    } else if (!hasParent) {
      if (theme.dataVerticalStyle == 'doublemenu') {
        // html.setAttribute('data-toggled', 'double-menu-close');
        // ThemeChanger({...theme, toggled: 'double-menu-close'});
        updateTheme({toggled: 'double-menu-close'});
      }
    }
  }
  function removeActiveOtherMenus(item: any) {
    if (item) {
      if (Array.isArray(item)) {
        for (const val of item) {
          val.active = false;
          val.selected = false;
        }
      }
      item.active = false;
      item.selected = false;

      if (item.children && item.children.length > 0) {
        removeActiveOtherMenus(item.children);
      }
    }
  }
  //
  const setMenuUsingUrl = useCallback(
    (currentPath: any) => {
      hasParent = false;
      hasParentLevel = 1;
      // Check current url and trigger the setSidemenu method to active the menu.
      const setSubmenuRecursively = (items: any) => {
        items?.forEach((item: any) => {
          if (item.path == '') {
          } else if (item.path === currentPath) {
            setSubmenu(null, item);
          }
          setSubmenuRecursively(item.children);
        });
      };
      setSubmenuRecursively(menuitems);
    },
    [menuitems]
  );
  const [previousUrl, setPreviousUrl] = useState('/');

  //
  function toggleSidemenu(event: any, targetObject: any, ADMINMENUITEMS = menuitems) {
    // const theme = store.getState().theme;
    const element = event.target;

    if (
      (window.screen.availWidth <= 992 || theme.dataNavStyle != 'icon-hover') &&
      (window.screen.availWidth <= 992 || theme.dataNavStyle != 'menu-hover')
    ) {
      for (const item of ADMINMENUITEMS) {
        if (isMenuTitleTs(item)) continue;
        if (item === targetObject) {
          if (theme.dataVerticalStyle == 'doublemenu' && item.active) {
            return;
          }
          item.active = !item.active;

          if (item.active) {
            closeOtherMenus(ADMINMENUITEMS, item);
          } else {
            if (theme.dataVerticalStyle == 'doublemenu') {
              updateTheme({toggled: 'double-menu-close'});
            }
          }
          setAncestorsActive(ADMINMENUITEMS, item);
        } else if (!item.active) {
          if (theme.dataVerticalStyle != 'doublemenu') {
            item.active = false; // Set active to false for items not matching the target
          }
        }
        if (item.children && item.children.length > 0) {
          toggleSidemenu(event, targetObject, item.children);
        }
      }
      if (targetObject?.children && targetObject.active) {
        if (theme.dataVerticalStyle == 'doublemenu' && theme.toggled != 'double-menu-open') {
          updateTheme({toggled: 'double-menu-open'});
        }
      }
      if (
        element &&
        theme.dataNavLayout == 'horizontal' &&
        (theme.dataNavStyle == 'menu-click' || theme.dataNavStyle == 'icon-click')
      ) {
        const listItem = element.closest('li');
        if (listItem) {
          // Find the first sibling <ul> element
          const siblingUL = listItem.querySelector('ul');
          let outterUlWidth = 0;
          let listItemUL = listItem.closest('ul:not(.main-menu)');
          while (listItemUL) {
            listItemUL = listItemUL.parentElement.closest('ul:not(.main-menu)');
            if (listItemUL) {
              outterUlWidth += listItemUL.clientWidth;
            }
          }
          if (siblingUL) {
            // You've found the sibling <ul> element
            const siblingULRect = listItem.getBoundingClientRect();
            if (theme.dir == 'rtl') {
              if (
                siblingULRect.left - siblingULRect.width - outterUlWidth + 150 < 0 &&
                outterUlWidth < window.innerWidth &&
                outterUlWidth + siblingULRect.width + siblingULRect.width < window.innerWidth
              ) {
                targetObject.dirchange = true;
              } else {
                targetObject.dirchange = false;
              }
            } else {
              if (
                outterUlWidth + siblingULRect.right + siblingULRect.width + 50 > window.innerWidth &&
                siblingULRect.right >= 0 &&
                outterUlWidth + siblingULRect.width + siblingULRect.width < window.innerWidth
              ) {
                targetObject.dirchange = true;
              } else {
                targetObject.dirchange = false;
              }
            }
          }
          setTimeout(() => {
            const computedValue = siblingUL.getBoundingClientRect();
            if (computedValue.bottom > window.innerHeight) {
              siblingUL.style.height = window.innerHeight - computedValue.top - 8 + 'px';
              siblingUL.style.overflow = 'auto';
            }
          }, 100);
        }
      }
    }
    setMenuitems((arr: any) => [...arr]);
  }

  function setAncestorsActive(ADMINMENUITEMS: any, targetObject: any) {
    const parent = findParent(ADMINMENUITEMS, targetObject);
    if (parent) {
      parent.active = true;
      if (parent.active) {
        updateTheme({toggled: 'double-menu-open'});
      }

      setAncestorsActive(ADMINMENUITEMS, parent);
    } else {
      if (theme.dataVerticalStyle == 'doublemenu') {
        updateTheme({toggled: 'double-menu-close'});
      }
    }
  }
  function closeOtherMenus(ADMINMENUITEMS: any, targetObject: any) {
    for (const item of ADMINMENUITEMS) {
      if (item !== targetObject) {
        item.active = false;
        if (item.children && item.children.length > 0) {
          closeOtherMenus(item.children, targetObject);
        }
      }
    }
  }

  function findParent(ADMINMENUITEMS: any, targetObject: any) {
    for (const item of ADMINMENUITEMS) {
      if (item.children && item.children.includes(targetObject)) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const parent: any = findParent((ADMINMENUITEMS = item.children), targetObject);
        if (parent) {
          return parent;
        }
      }
    }
    return null;
  }

  const Sideclick = () => {
    // if (window.innerWidth > 992) {
    //   const html = document.documentElement;
    //   if (html.getAttribute('icon-overlay') != 'open') {
    //     html.setAttribute('icon-overlay', 'open');
    //   }
    // }
  };

  const handleAttributeChange = useCallback(
    (mutationsList: any) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-nav-layout' || mutation.attributeName === 'data-vertical-style')
        ) {
          const newValue = mutation.target.getAttribute('data-nav-layout');
          if (newValue == 'vertical') {
            setMenuUsingUrl(currentPath || '/dashboard/ecommerce');
          } else {
            closeMenuFn();
          }
        }
      }
    },
    [currentPath, setMenuUsingUrl, closeMenuFn]
  );

  useEffect(() => {
    // Select the target element
    const targetElement = document.documentElement;

    // Create a MutationObserver instance
    const observer = new MutationObserver(handleAttributeChange);

    // Configure the observer to watch for attribute changes
    const config = {attributes: true};

    // Start observing the target element
    observer.observe(targetElement, config);

    if (currentPath !== previousUrl) {
      setMenuUsingUrl(currentPath);
      setPreviousUrl(currentPath);
    }
  }, [location, currentPath, handleAttributeChange, previousUrl, setMenuUsingUrl]);

  const canAccessAdminLink = (levelOne: menuItemT) => {
    if (levelOne.permission === 'admin') {
      return isAdmin || isSuperAdmin;
    }
    if (levelOne.permission === 'super') {
      return isSuperAdmin;
    }
    return true; // For links without specific permissions
  };

  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    const handleMainContentClick = () => {
      if (window.innerWidth <= 992 && theme.toggled !== 'close') {
        menuClose();
      }
    };

    mainContent?.addEventListener('click', handleMainContentClick);

    return () => {
      mainContent?.removeEventListener('click', handleMainContentClick);
    };
  }, [menuClose, theme.toggled]);

  const sidebarClass = `app-sidebar ${
    theme.dataVerticalStyle === 'overlay'
      ? 'overlay-sidebar'
      : theme.toggled === 'close-menu-close'
        ? 'closed-sidebar'
        : ''
  }`;

  return (
    <Fragment>
      <div
        id="responsive-overlay"
        className={theme.toggled === 'open' ? 'active' : ''}
        onClick={() => toggleSidebar()}
      />
      <aside className={sidebarClass} id="sidebar" onMouseEnter={() => Onhover()} onMouseLeave={() => Outhover()}>
        <div className="main-sidebar-header">
          <a href={`/`} className="header-logo">
            <img src="/images/logo/logo.png" alt="logo" className="desktop-logo" />
            <img src="/images/logo/logo.png" alt="logo" className="toggle-logo" />
            <img src="/images/logo/logo.png" alt="logo" className="desktop-dark" />
            <img src="/images/logo/logo.png" alt="logo" className="toggle-dark" />
            <img src="/images/logo/logo.png" alt="logo" className="desktop-white" />
            <img src="/images/logo/logo.png" alt="logo" className="toggle-white" />
          </a>
        </div>
        <SimpleBar className="main-sidebar" id="sidebar-scroll">
          <nav className="main-menu-container nav nav-pills flex-column sub-open">
            <div
              className="slide-left"
              id="slide-left"
              onClick={() => {
                slideLeft();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="#7b8191" width="24" height="24" viewBox="0 0 24 24">
                <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"></path>
              </svg>
            </div>

            <ul className="main-menu" onClick={() => Sideclick()}>
              {/* a toggle button if the user isAdministrator */}
              {isAdministrator && (
                // a toggle button to swtich between acting as speculator and acting as admin
                <li className="slide__category">
                  <button className="side-menu__item" onClick={handleToggleAccount}>
                    <i className="side-menu__icon bx bx-transfer"></i>
                    <span className="side-menu__label">{actingAs === 'speculator' ? 'Admin' : 'Speculator'}</span>
                  </button>
                </li>
              )}
              {menuitems.map((levelOne: any) => (
                <Fragment key={Math.random()}>
                  <li
                    className={`${levelOne.menutitle ? 'slide__category' : ''} ${levelOne.type === 'link' ? 'slide' : ''} ${levelOne.type === 'sub' ? 'slide has-sub' : ''} ${levelOne?.active ? 'open' : ''} ${levelOne?.selected || levelOne?.path === currentPath ? 'active' : ''}`}
                  >
                    {levelOne.menutitle ? <span className="category-name">{levelOne.menutitle}</span> : ''}
                    {levelOne.type === 'link' && canAccessAdminLink(levelOne) ? (
                      <Link
                        to={levelOne.path + '/'}
                        className={`side-menu__item ${levelOne.selected || levelOne?.path === currentPath ? 'active' : ''}`}
                      >
                        {levelOne.icon}
                        <span className="side-menu__label">{levelOne.title}</span>
                      </Link>
                    ) : (
                      ''
                    )}
                    {levelOne.type === 'empty' ? (
                      <Link to="#" className="side-menu__item">
                        {levelOne.icon}
                        <span className="">{levelOne.title}</span>
                      </Link>
                    ) : (
                      ''
                    )}
                    {levelOne.type === 'sub' ? (
                      <Menuloop ADMINMENUITEMS={levelOne} level={level + 1} toggleSidemenu={toggleSidemenu} />
                    ) : (
                      ''
                    )}
                  </li>
                </Fragment>
              ))}
            </ul>
            <div
              className="slide-right"
              id="slide-right"
              onClick={() => {
                slideRight();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="#7b8191" width="24" height="24" viewBox="0 0 24 24">
                <path d="M10.707 17.707 16.414 12l-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"></path>
              </svg>
            </div>
          </nav>
        </SimpleBar>
      </aside>
    </Fragment>
  );
};

export default Sidebar;
